import type { ProtectionExperimentGroup } from 'checkout';

type EventWithParams<TEvent extends string, TParams extends Record<string, any>> = {
  event: TEvent;
  params: TParams;
};

type GoogleEventWithParams<
  TEvent extends Gtag.EventNames,
  TParams extends Gtag.EventParams
> = EventWithParams<TEvent, TParams>;

export type AnalyticsEvent =
  | EventWithParams<'basket_expired', BasketExpiredParams>
  | EventWithParams<'email_captured', EmailCapturedParams>
  | EventWithParams<'protection_offered', ProtectionOfferedParams>
  | EventWithParams<'protection_eligible', ProtectionEligibleParams>
  | GoogleEventWithParams<'add_to_cart', AddToCartParameters>
  | GoogleEventWithParams<'view_cart', ViewCartParameters>
  | GoogleEventWithParams<'purchase', PurchaseParameters>
  | GoogleEventWithParams<'remove_from_cart', RemoveFromCartParameters>
  | GoogleEventWithParams<'begin_checkout', BeginCheckoutParameters>
  | GoogleEventWithParams<'add_payment_info', AddPaymentInfoParameters>
  | GoogleEventWithParams<'view_item', ViewItemParameters>
  | GoogleEventWithParams<'view_item_list', ItemListParameters>
  | GoogleEventWithParams<'select_item', ItemListParameters>
  | EventWithParams<'experiment_viewed', GrowthbookExperimentParameters>
  // denial occurs when a user arrives on the room/rate/spa item page and there is no availability
  | EventWithParams<'denial', DenialParams>
  // listing_ready occurs once per listing view and search, when the cards are shown and nothing is
  // loading; wait_ms is the time the search spent loading (skeleton or spinner), idle time excluded
  | EventWithParams<'listing_ready', ListingReadyParams>
  | EventWithParams<'upgrade_selection', UpgradeSelectionParams>
  | EventWithParams<'upgrade_offered', UpgradeOfferedParams>;

type ItemListParameters = Required<Pick<Gtag.EventParams, 'items'>> & { item_list_name: string };

export type ListingReadyParams = {
  item_list_name: string;
  wait_ms: number;
  package_count: number;
  package_checks_timed_out: number;
  full_package_check: boolean;
};

type BasketExpiredParams = Required<Pick<Gtag.EventParams, 'currency' | 'value'>> & {
  basket_id: string;
};

type ProtectionOfferedParams = Required<Pick<Gtag.EventParams, 'currency' | 'value'>> & {
  basket_id: string;
};

type ProtectionEligibleParams = ProtectionOfferedParams & {
  experiment_group: ProtectionExperimentGroup;
};

type EmailCapturedParams = {
  basket_id: string;
  capture_point: 'registration' | 'billing';
};

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
type AddToCartParameters = Required<Pick<Gtag.EventParams, 'currency' | 'value' | 'items'>> & {
  check_in?: string;
};

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
export type PurchaseParameters = Required<
  Pick<Gtag.EventParams, 'currency' | 'transaction_id' | 'value' | 'items'>
> &
  Pick<Gtag.EventParams, 'coupon' | 'shipping' | 'tax'>;

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
type ViewItemParameters = Omit<Required<Pick<Gtag.EventParams, 'items'>>, 'items'> &
  Pick<Gtag.EventParams, 'currency' | 'value'> & {
    //Start date and end date are specifically defined for events that are NOT item specific
    start_date?: string;
    end_date?: string;
    adults?: number;
    children?: number;
    items: ExtendedViewItem[];
  };

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_cart
type ViewCartParameters = Required<Pick<Gtag.EventParams, 'currency' | 'value' | 'items'>>;

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#remove_from_cart
type RemoveFromCartParameters = Required<Pick<Gtag.EventParams, 'currency' | 'value' | 'items'>>;

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
type BeginCheckoutParameters = Required<Pick<Gtag.EventParams, 'currency' | 'value' | 'items'>> &
  Pick<Gtag.EventParams, 'coupon'>;

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_payment_info
type AddPaymentInfoParameters = Required<Pick<Gtag.EventParams, 'currency' | 'value' | 'items'>> &
  Pick<Gtag.EventParams, 'coupon'> & {
    payment_type?: string;
  };

export enum DenialReason {
  NO_AVAILABILITY = 'no_availability',
  INTEGRATION_UNAVAILABLE = 'integration_unavailable',
  PACKAGE_UNAVAILABLE = 'package_unavailable',
}

type DenialParams = Required<Pick<Gtag.EventParams, 'items'>> & {
  start_date: string;
  end_date: string;
  reason: DenialReason;
};

type GtagPrice = {
  amount: number;
  currencyCode: string;
};

type GtagUpgradeItem = Gtag.Item & {
  original_item_id?: string;
  original_item_name?: string;
  price_difference: GtagPrice;
  price_per_stay: GtagPrice;
  accepted: boolean;
};

export type UpgradeSource = 'booking_step' | 'basket';

type UpgradeSelectionParams = {
  items: GtagUpgradeItem[];
  upgrade_source?: UpgradeSource;
};

type UpgradeOfferedParams = {
  items: Omit<GtagUpgradeItem, 'accepted'>[];
  upgrade_source: UpgradeSource;
  list_location: 'basket_page' | 'basket_drawer';
};

//Check in and check out are specifically defined for events that ARE item specific
type ExtendedViewItem = Gtag.Item & {
  check_in?: string;
  check_out?: string;
  adults?: number;
  children?: number;
};

type GrowthbookExperimentParameters = {
  experiment_id: string;
  variation_id: number;
  site_slug: string;
};
